// Importing createContext to create a new React context and useContext to consume it
import { createContext, useContext } from 'react';
// Importing the User type for strong typing of the user object
import type { User } from '../types';

// Defining the structure of the authentication context
interface AuthContextType {
  // The currently authenticated user or null if not logged in
  user: User | null;
  // Boolean flag indicating if authentication data is currently being loaded
  isLoading: boolean;
  // Function to log in a user with username and password
  // Returns a Promise since authentication may involve async operations
  login: (username: string, password: string) => Promise<void>;
  // Function to log out the current user
  // Returns a Promise since logout may involve async operations
  logout: () => Promise<void>;
  // Function to refresh the user data, e.g., fetch updated user details from backend
  // Returns a Promise since fetching is asynchronous
  refreshUser: () => Promise<void>;
}
// Creating the authentication context with an initial value of undefined
// The generic type ensures the context can only contain AuthContextType or undefined
export const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Custom hook to access the authentication context
export function useAuthContext() {
  // Using useContext to consume AuthContext
  const context = useContext(AuthContext);
  // Throwing an error if the hook is used outside the AuthProvider
  // This enforces proper usage and avoids null/undefined access issues
  if (!context) throw new Error('useAuthContext must be used within AuthProvider');
  // Returning the context value (user, isLoading, login, logout, refreshUser)
  return context;
}
